import type { ChatToolCall } from '../api/domains/chatbot.api'
import { announceLibraryChange } from '../library/library-refresh'

/**
 * Assistant tools that change forms or submissions on the server. When a finished turn carries a
 * successful one, the /forms pages re-read: `apps/console` has no query cache to invalidate, so
 * this module-level signal stands in for it (the same shape as `geocoding-refresh.ts`).
 * `edit_form` is NOT here: it changes only the open builder, never the server.
 */
export const FORMS_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'create_form',
  'publish_form',
  'share_form',
  'retry_submission',
  'edit_submission',
  'delete_submission',
  // Both change the form's status (archived / draft): the list drops or regains the form on re-read.
  'archive_form',
  'unarchive_form',
])

export interface FormsChange {
  /** Forms that changed or were created. */
  formIds: string[]
  /** Submissions that changed. */
  submissionIds: string[]
  /** Submissions that no longer exist. */
  deletedSubmissionIds: string[]
}

type Listener = (change: FormsChange) => void

const listeners = new Set<Listener>()

export function subscribeFormsRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

interface WriteResult {
  success?: unknown
  form_id?: unknown
  submission_id?: unknown
  deleted?: unknown
  layer_id?: unknown
}

const idOf = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)

/** A result counts only when `success === true`; refusals and reads change nothing. */
export function announceFormsWrites(toolCalls: readonly ChatToolCall[]): void {
  const formIds: string[] = []
  const submissionIds: string[] = []
  const deletedSubmissionIds: string[] = []
  const layerIds: string[] = []

  for (const call of toolCalls) {
    if (!FORMS_WRITE_TOOLS.has(call.name)) continue
    const result = call.result as WriteResult | null

    if (!result || typeof result !== 'object' || result.success !== true) continue

    const formId = idOf(result.form_id)
    const submissionId = idOf(result.submission_id)

    if (formId) formIds.push(formId)

    if (submissionId) {
      if (call.name === 'delete_submission' && result.deleted === true) deletedSubmissionIds.push(submissionId)
      else submissionIds.push(submissionId)
    }

    // A published form with a new target layer is a new Library item.
    const layer = call.name === 'publish_form' ? idOf(result.layer_id) : null
    if (layer) layerIds.push(layer)
  }

  if (layerIds.length > 0) announceLibraryChange({ changedIds: layerIds, deletedIds: [] })
  if (formIds.length === 0 && submissionIds.length === 0 && deletedSubmissionIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener({ formIds, submissionIds, deletedSubmissionIds })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}

/** True when a change names this form. */
export function changeTouchesForm(change: FormsChange, formId: string | null | undefined): boolean {
  if (!formId) return false

  return change.formIds.includes(formId)
}
